import React, { useCallback, useEffect, useMemo, useState } from "react";
import { arrayOf, bool, func, oneOfType, number, shape, string } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";
import moment from "moment";
import { isIOS } from "react-device-detect";

import { AccountConfirmation, AccountOpeningTermsDrawer, Spinner } from "@ui-kit/components/index.es";
import { resizableRoute } from "pages/_components/Resizable";
import I18n from "pages/_components/I18n";
import { actions as formActions, selectors as formSelectors } from "reducers/form";
import { actions as layoutActions } from "reducers/layout";
import { actions as loginActions, selectors as loginSelectors } from "reducers/login";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { actions as tokenActions, selectors as tokenSelectors } from "reducers/token";
import { selectors as sessionSelectors } from "reducers/session";

import { CURRENCY_CODE } from "constants.js";
import { flattenArray, removeDuplicateItems } from "util/array";
import * as configUtils from "util/config";
import * as i18n from "util/i18n";
import isTokenActive from "util/token";
import { dispatchV3ShellLayout } from "util/v3ShellLayoutSync";
import formatLegacyWebDateTimeString from "util/formatLegacyWebDateTimeString";
import {
    resolveAccountOpeningTyCAccountTypeLabel,
    resolveAccountOpeningTyCCurrencyLabel,
    resolveAccountOpeningTyCTermsDocumentKey,
} from "util/accountOpeningTyC";

function resolveV3SummaryGoBackPath(openingData) {
    if (openingData?.returnToPending) {
        return "/v3/accounts/edit";
    }
    return "/v3/accounts/create";
}

/** Misma composición que {@link TransactionTicketSignConfirmation} → `credentials` en `signTransaction`. */
function mapSignCredentialFieldNames(credentialsGroups) {
    if (!Array.isArray(credentialsGroups) || credentialsGroups.length === 0) {
        return [];
    }
    return compose(
        (names) => names.filter((item) => item !== "accessToken"),
        removeDuplicateItems,
        flattenArray,
        (groups) => groups.map(({ credentials }) => credentials),
    )(credentialsGroups);
}

function AccountOpeningSummaryV3Page({
    dispatch,
    isDesktop,
    openingData,
    fullName,
    userId,
    isRetailEnvironment,
    fetchingDownload,
    location,
    signCredentialFieldNames,
    signTransactionData,
    signFormFetching,
    cancelTransactionData,
    tokenNumber,
    fetchingToken,
}) {
    const [showTermsDrawer, setShowTermsDrawer] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);

    const isMobileWithToken = !isDesktop && isTokenActive();

    const goBackPath = useMemo(() => {
        if (
            (openingData?.action === "sign" || openingData?.action === "cancel") &&
            typeof location?.state?.goBackState === "string" &&
            location.state.goBackState.startsWith("/")
        ) {
            return location.state.goBackState;
        }
        return resolveV3SummaryGoBackPath(openingData);
    }, [location, openingData]);

    const enableTyC = useMemo(
        () =>
            (openingData?.accountType === 3 && `${openingData?.currency}` === CURRENCY_CODE.USD) ||
            configUtils.getBoolean("frontend.request.checkingAccount.Currency.functionalities", false),
        [openingData],
    );

    useEffect(() => {
        if (!openingData || !Object.keys(openingData).length) {
            dispatch(push("/v3/accounts/create"));
        }
    }, [dispatch, openingData]);

    useEffect(() => {
        if (isMobileWithToken) {
            dispatch(tokenActions.softTokenGenerationRequest());
        }
    }, [dispatch, isMobileWithToken]);

    const goBack = useCallback(() => {
        dispatch(push(goBackPath));
    }, [dispatch, goBackPath]);

    const getLabels = useCallback((id) => i18n.get(id), []);

    const rateformUrl = useMemo(
        () => configUtils.get("client.account.opening.rateform", "https://www.bancocmf.com.ar/tarifario"),
        [],
    );

    const navigateToCMF = useCallback(() => {
        if (!rateformUrl) {
            return;
        }
        if (!window.cordova) {
            window.open(rateformUrl, "_blank", "noopener noreferrer");
        } else if (!isIOS) {
            window.navigator.app.loadUrl(rateformUrl, { openExternal: true });
        } else if (window.cordova) {
            window.cordova.InAppBrowser.open(
                rateformUrl,
                "_system",
                "hidden=no,location=no,clearsessioncache=yes,clearcache=yes",
            );
        }
    }, [rateformUrl]);

    const toggleTermsDrawer = useCallback(() => {
        setShowTermsDrawer((prev) => !prev);
    }, []);

    const getToken = useCallback(async () => {
        if (isMobileWithToken && tokenNumber) {
            return { token: tokenNumber };
        }
        return { token: null };
    }, [isMobileWithToken, tokenNumber]);

    const handleConfirm = useCallback(
        (values, formikBag) => {
            const isSignFlow = openingData?.action === "sign";
            const wrappedBag = {
                ...formikBag,
                setSubmitting: (v) => {
                    formikBag.setSubmitting(v);
                    setConfirmLoading(Boolean(v));
                },
                setErrors: (errs) => {
                    formikBag.setErrors(errs);
                },
            };

            if (isSignFlow) {
                const fieldNames =
                    Array.isArray(signCredentialFieldNames) && signCredentialFieldNames.length > 0
                        ? signCredentialFieldNames
                        : ["otp"];
                const errors = {};
                let hasErr = false;
                fieldNames.forEach((name) => {
                    const raw = values?.[name];
                    const trimmed = raw != null ? String(raw).trim() : "";
                    if (!trimmed) {
                        hasErr = true;
                        errors[name] = i18n.get(
                            name === "otp"
                                ? "accounts.new.account.otp.error.required"
                                : `form.credential.${name}.required`,
                        );
                    }
                });
                if (hasErr) {
                    const touched = {};
                    fieldNames.forEach((name) => {
                        touched[name] = true;
                    });
                    formikBag.setErrors(errors);
                    formikBag.setTouched(touched);
                    formikBag.setSubmitting(false);
                    return Promise.resolve();
                }
                const credentials = fieldNames.reduce((acc, name) => {
                    acc[name] = String(values[name]).trim();
                    return acc;
                }, {});
                dispatch(
                    formActions.signTransaction({
                        credentials,
                        formikBag: wrappedBag,
                        idTransaction: signTransactionData?.idTransaction ?? openingData?.idTransaction,
                        idForm: signTransactionData?.idForm ?? "",
                        idActivity: signTransactionData?.idActivity ?? openingData?.idActivity ?? "accounts.opening",
                        userId,
                        pendingDispatch: signTransactionData?.pendingDispatch ?? openingData?.pendingDispatch,
                        useTransactionData: signTransactionData?.useTransactionData,
                    }),
                );
                return Promise.resolve();
            }

            if (openingData?.action === "cancel") {
                const fieldNames =
                    Array.isArray(signCredentialFieldNames) && signCredentialFieldNames.length > 0
                        ? signCredentialFieldNames
                        : ["otp"];
                const errors = {};
                let hasErr = false;
                fieldNames.forEach((name) => {
                    const raw = values?.[name];
                    const trimmed = raw != null ? String(raw).trim() : "";
                    if (!trimmed) {
                        hasErr = true;
                        errors[name] = i18n.get(
                            name === "otp"
                                ? "accounts.new.account.otp.error.required"
                                : `form.credential.${name}.required`,
                        );
                    }
                });
                if (hasErr) {
                    const touched = {};
                    fieldNames.forEach((name) => {
                        touched[name] = true;
                    });
                    formikBag.setErrors(errors);
                    formikBag.setTouched(touched);
                    formikBag.setSubmitting(false);
                    return Promise.resolve();
                }
                const credentials = fieldNames.reduce((acc, name) => {
                    acc[name] = String(values[name]).trim();
                    return acc;
                }, {});
                dispatch(
                    formActions.cancelTransaction({
                        credentials,
                        formikBag: wrappedBag,
                        idTransaction: cancelTransactionData?.idTransaction ?? openingData?.idTransaction,
                        userId,
                        rejectedTransactions: false,
                        force: cancelTransactionData?.force,
                    }),
                );
                return Promise.resolve();
            }

            const otp = values?.otp != null ? String(values.otp).trim() : "";
            if (!otp) {
                formikBag.setErrors({ otp: i18n.get("accounts.new.account.otp.error.required") });
                formikBag.setTouched({ otp: true });
                formikBag.setSubmitting(false);
                return Promise.resolve();
            }
            if (enableTyC && !values?.acceptTerms) {
                formikBag.setErrors({
                    acceptTerms: i18n.get("enrollment.index.acceptTerms.required"),
                });
                formikBag.setTouched({ acceptTerms: true });
                formikBag.setSubmitting(false);
                return Promise.resolve();
            }

            let fechaHoraTyC = "";
            let userName = "";
            let termsAndConditions = "";
            const acceptTerms = values?.acceptTerms;
            const payloadBase = {
                ...openingData,
                idActivity: "accounts.opening",
                acceptTerms,
            };

            // Formik clears submitting on return unless a Promise is returned; defer resolve until saga ends.
            return new Promise((resolveDefer) => {
                Object.assign(wrappedBag, { _deferFormikSubmitUntilOpened: resolveDefer });
                if (enableTyC) {
                    fechaHoraTyC = moment(Date())
                        .format("DD/MM/YYYY HH:mm:ss")
                        .toString();
                    userName = fullName;
                    termsAndConditions = acceptTerms ? "Aceptado" : "";
                    dispatch(
                        accountsActions.openingAccount(
                            {
                                ...payloadBase,
                                userName,
                                fechaHoraTyC,
                                termsAndConditions,
                            },
                            otp,
                            userId,
                            wrappedBag,
                        ),
                    );
                } else {
                    dispatch(accountsActions.openingAccount(payloadBase, otp, userId, wrappedBag));
                }
            });
        },
        [
            dispatch,
            enableTyC,
            fullName,
            openingData,
            userId,
            signCredentialFieldNames,
            signTransactionData,
            cancelTransactionData,
        ],
    );

    const isAccountOpeningSignatureFlow = openingData?.action === "sign";
    const isAccountOpeningCancelFlow = openingData?.action === "cancel";

    const confirmation = useMemo(
        () => ({
            confirmationText: String(
                !isTokenActive(!isDesktop)
                    ? i18n.get("transfer.summary.confirm.label")
                    : i18n.get("confirmation.withoutotp.text") || "",
            ),
            handleConfirm,
            loading:
                confirmLoading || ((isAccountOpeningSignatureFlow || isAccountOpeningCancelFlow) && signFormFetching),
            isInactive: false,
            getToken,
        }),
        [
            confirmLoading,
            handleConfirm,
            isDesktop,
            isAccountOpeningCancelFlow,
            isAccountOpeningSignatureFlow,
            signFormFetching,
            getToken,
        ],
    );

    useEffect(() => {
        if (!openingData || !Object.keys(openingData).length) {
            return undefined;
        }
        const headerTitle = isAccountOpeningCancelFlow
            ? i18n.get("new.account")
            : i18n.get(openingData?.returnToPending ? "modify.account" : "new.account");
        dispatchV3ShellLayout(dispatch, {
            headerTitle,
            titleKey: "",
            goBackPath,
            scopes: isAccountOpeningCancelFlow ? ["cancelTransaction"] : ["accountOpeningSummary"],
        });
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setTitleKey(""));
            dispatch(layoutActions.setHeaderTitle(""));
            dispatch(layoutActions.setHeaderSubtitle(""));
            dispatch(layoutActions.setGoBackPath(null));
        };
    }, [dispatch, goBackPath, isAccountOpeningCancelFlow, openingData]);

    const hidePageHeaderWhenShellShowsEmbeddedTitle = !isDesktop;

    const termsDocumentKey = useMemo(
        () => resolveAccountOpeningTyCTermsDocumentKey(openingData?.accountType, isRetailEnvironment),
        [openingData?.accountType, isRetailEnvironment],
    );
    const termsDrawerTitle = useMemo(
        () =>
            i18n.get("accounts.new.termsAndConditions.title", null, {
                tipoCuenta: resolveAccountOpeningTyCAccountTypeLabel(openingData?.accountType),
                moneda: resolveAccountOpeningTyCCurrencyLabel(openingData?.currency),
            }),
        [openingData?.accountType, openingData?.currency],
    );

    const pageTitle = isAccountOpeningCancelFlow
        ? i18n.get("new.account")
        : i18n.get(openingData?.returnToPending ? "modify.account" : "new.account");
    const confirmationSubtitle = isAccountOpeningCancelFlow
        ? i18n.get("cancel.check.transaction")
        : i18n.get("account.opening.summary.checkInfo.label");

    if (!openingData || !Object.keys(openingData).length) {
        return null;
    }

    const withTermsAndTariff = enableTyC && openingData?.action !== "sign" && openingData?.action !== "cancel";
    const isTokenPending = isMobileWithToken && (fetchingToken || !tokenNumber);

    if (isTokenPending) {
        return <Spinner size="large" />;
    }

    return (
        <>
            <AccountConfirmation
                goBack={goBack}
                hideDesktopPageHeader={hidePageHeaderWhenShellShowsEmbeddedTitle}
                title={pageTitle}
                subtitle={confirmationSubtitle}
                getLabels={getLabels}
                openingData={openingData}
                userFullName={fullName}
                formatSignatureDate={formatLegacyWebDateTimeString}
                withTermsAndTariff={withTermsAndTariff}
                onOpenTerms={toggleTermsDrawer}
                onDownloadTariff={navigateToCMF}
                confirmation={confirmation}
            />
            {withTermsAndTariff ? (
                <AccountOpeningTermsDrawer
                    isVisible={showTermsDrawer}
                    onClose={toggleTermsDrawer}
                    title={termsDrawerTitle}
                    downloadLabel={i18n.get("global.download")}
                    downloadAriaLabel={i18n.get("global.download")}
                    isDownloadLoading={fetchingDownload}
                    onDownload={() => dispatch(loginActions.downloadDocumentI18n("pdf", termsDocumentKey))}>
                    <I18n component="p" id={termsDocumentKey} />
                </AccountOpeningTermsDrawer>
            ) : null}
        </>
    );
}

AccountOpeningSummaryV3Page.propTypes = {
    dispatch: func.isRequired,
    isDesktop: bool.isRequired,
    location: shape({}),
    openingData: shape({}),
    fullName: string.isRequired,
    userId: oneOfType([string, number]),
    isRetailEnvironment: bool.isRequired,
    fetchingDownload: bool.isRequired,
    signCredentialFieldNames: arrayOf(string),
    signFormFetching: bool.isRequired,
    signTransactionData: shape({}),
    cancelTransactionData: shape({}),
    tokenNumber: string,
    fetchingToken: bool,
};

AccountOpeningSummaryV3Page.defaultProps = {
    openingData: null,
    userId: null,
    location: null,
    signCredentialFieldNames: [],
    signTransactionData: null,
    cancelTransactionData: null,
    tokenNumber: null,
    fetchingToken: false,
};

const mapStateToProps = (state) => ({
    openingData: accountsSelectors.getAccountOpeningData(state),
    fullName: sessionSelectors.getUser(state).userFullName,
    userId: sessionSelectors.getUser(state).userId,
    isRetailEnvironment: sessionSelectors.isRetailEnvironment(state),
    fetchingDownload: loginSelectors.getFetchingDownload(state),
    signCredentialFieldNames: mapSignCredentialFieldNames(formSelectors.getCredentialsGroups(state)),
    signTransactionData: formSelectors.getSignTransactionData(state),
    signFormFetching: formSelectors.getFetching(state),
    cancelTransactionData: formSelectors.getCancelTransactionData(state),
    tokenNumber: tokenSelectors.getTokenNumber(state),
    fetchingToken: tokenSelectors.getFetchingToken(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(AccountOpeningSummaryV3Page);
